import React, { ForwardedRef, forwardRef, useEffect, useState } from "react"
import { NodeResult, NodeResults } from "@custom-types/modelRun"
import { Scatter } from "react-chartjs-2"
import "chart.js/auto"
import { Box, Grid, Typography } from "@mui/material"
import { Chart, ChartDataset } from "chart.js"
import annotationPlugin from "chartjs-plugin-annotation"
import { color_tones, lighter_rgba, pearsonCorrelationCoef, to_rgba } from "./const"
import * as d3 from "d3"
import { formatValuePythonic } from "@utils/format/pythonicFormat"
import { generateChartLabel } from "@utils/chartLabelUtils"

Chart.register(annotationPlugin)

type CorrelogramScatterProps = {
  selectedTimesteps: (number | string)[]
  datasets: NodeResults
  formats: Map<string, string>
  version?: string
}

type AnnotationType = {
  xMin?: number
  yMin?: number
  xMax?: number
  yMax?: number
  content?: () => string[]
}

type ChartDataType = {
  labels: string[]
  datasets: ChartDataset<"scatter", { x: number; y: number }[]>[]
  annotationX: AnnotationType
  annotationY: AnnotationType
}

export const CorrelogramScatter = forwardRef(
  (
    { datasets, selectedTimesteps, formats, version }: CorrelogramScatterProps,
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    const dataSetsArray = Object.values(datasets)
    const [chartData, setChartData] = useState<ChartDataType>({
      labels: [],
      datasets: [],
      annotationX: {},
      annotationY: {},
    })

    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ANY :(
    const [multiChartData, setMultiChartData] = useState<any[][]>([])
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ANY :(
    const scatterChartOptons: any = {
      scales: {
        x: {
          title: {
            display: true,
          },
        },
        y: {
          title: {
            display: true,
          },
        },
      },
      plugins: {
        legend: {
          labels: {
            boxWidth: 11,
            boxHeight: 11,
            font: {
              size: 11,
              style: "Akkurat-Bold",
            },
          },
        },
        annotation: {
          annotations: {
            line1: {
              type: "line",
              // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
              borderColor: to_rgba(lighter_rgba(color_tones[2], 0, 0.5)),
              borderWidth: 2,
              label: {
                display: true,
                backgroundColor: "rgba(102, 102, 102, 0.5)",
                color: "white",
                shadowBlur: 1,
                borderJoinStyle: "round",
                borderWidth: 0,
                radius: 10,
                zIndex: 300,
                position: "start",
                yAdjust: -5,
                rotation: 0,
                content: undefined,
              },
              font: {
                size: 14,
              },
              backgroundColor: "rgba(245,245,245)",
            },
            line2: {
              type: "line",
              // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
              borderColor: to_rgba(lighter_rgba(color_tones[2], 0, 0.5)),
              borderWidth: 2,
              label: {
                display: true,
                backgroundColor: "rgba(102, 102, 102, 0.5)",
                color: "white",
                shadowBlur: 1,
                borderJoinStyle: "round",
                borderWidth: 0,
                radius: 10,
                zIndex: 300,
                position: "start",
                yAdjust: -5,
                rotation: 0,
                content: undefined,
              },
              font: {
                size: 14,
              },
              backgroundColor: "rgba(245,245,245)",
            },
          },
        },
      },
    }

    function createScatterDataset(
      dataset1: NodeResult[],
      dataset2: NodeResult[],
    ): {
      label: string
      data: { x: number; y: number }[]
      annotationX: AnnotationType
      annotationY: AnnotationType
    } {
      if (!dataset1?.length || !dataset2?.length) {
        return {
          label: "",
          data: [],
          annotationX: {},
          annotationY: {},
        }
      }

      const data: { x: number; y: number }[] = []
      dataset1.forEach((item) => {
        if (selectedTimesteps.includes(item.timestep)) {
          const secondNodeDataset = dataset2.find((x) => x.timestep === item.timestep)
          if (secondNodeDataset) {
            item.data.forEach((dataItem, index) => {
              // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
              data.push({ x: dataItem, y: secondNodeDataset.data[index] })
            })
          }
        }
      })

      const allX = data?.map((x: { x: number; y: number }) => x.x) || []
      const allY = data?.map((x: { x: number; y: number }) => x.y) || []
      const correlationCoeffs = pearsonCorrelationCoef(allX, allY)

      const minX = +(d3.min(allX) || 0)
      const maxX = +(d3.max(allX) || 0)
      const minY = +(d3.min(allY) || 0)
      const maxY = +(d3.max(allY) || 0)
      const label1 = generateChartLabel({
        nodeName: dataset1?.[0]?.node_key ?? "",
        nodeUnit: dataset1?.[0]?.unit_format,
        numberOfRuns: 1,
      })
      const label2 = generateChartLabel({
        nodeName: dataset2?.[0]?.node_key ?? "",
        nodeUnit: dataset2?.[0]?.unit_format,
        numberOfRuns: 1,
      })
      return {
        label: `${label1} - ${label2}`,
        data: data,
        annotationX: {
          xMin: minX,
          yMin: correlationCoeffs.B0Y + correlationCoeffs.B1Y * minX,
          xMax: maxX,
          yMax: correlationCoeffs.B0Y + correlationCoeffs.B1Y * maxX,
          content: () => [`r = ${correlationCoeffs.coef}`],
        },
        annotationY: {
          xMin: correlationCoeffs.B0X + correlationCoeffs.B1X * minY,
          yMin: minY,
          xMax: correlationCoeffs.B0X + correlationCoeffs.B1X * maxY,
          yMax: maxY,
          content: undefined,
        },
      }
    }

    useEffect(() => {
      if (dataSetsArray.length === 2) {
        // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
        const chartDataset = createScatterDataset(dataSetsArray[0], dataSetsArray[1])
        setChartData({
          labels: [chartDataset.label],
          datasets: [chartDataset],
          annotationX: chartDataset.annotationX,
          annotationY: chartDataset.annotationY,
        })
      } else {
        const chartDatasets: {
          labels?: string[]
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ANY :(
          datasets?: any[]
          annotationX?: AnnotationType
          annotationY?: AnnotationType
        }[][] = []

        dataSetsArray.forEach((itemX, indexX) => {
          chartDatasets.push([])
          dataSetsArray.forEach((itemY, indexY) => {
            if (indexX === indexY) {
              // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
              chartDatasets[indexX].push({})
            } else {
              const chartDataset = createScatterDataset(
                // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
                dataSetsArray[indexX],
                dataSetsArray[indexY],
              )
              // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
              chartDatasets[indexX].push({
                labels: [chartDataset.label],
                datasets: [chartDataset],
                annotationX: chartDataset.annotationX,
                annotationY: chartDataset.annotationY,
              })
            }
          })
        })

        setMultiChartData(chartDatasets)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps -- FIX: deps after bulk migration
    }, [datasets, selectedTimesteps])

    if (dataSetsArray.length == 2) {
      const annotations = scatterChartOptons.plugins.annotation.annotations
      annotations.line1.label.content = chartData.annotationX?.content
      annotations.line2.label.content = chartData.annotationY?.content
      annotations.line1 = {
        ...annotations.line1,
        ...chartData.annotationX,
      }
      annotations.line2 = {
        ...annotations.line2,
        ...chartData.annotationY,
      }

      scatterChartOptons.scales.x.title.text = generateChartLabel({
        nodeName: dataSetsArray[0]?.[0]?.node_key ?? "",
        nodeUnit: dataSetsArray[0]?.[0]?.unit_format,
        numberOfRuns: 1,
      })
      scatterChartOptons.scales.x.ticks = {
        callback: function (value: string) {
          return formatValuePythonic(value, formats.get(dataSetsArray[0]?.[0]?.node_key || ""))
        },
      }
      scatterChartOptons.scales.y.title.text = generateChartLabel({
        nodeName: dataSetsArray[1]?.[0]?.node_key ?? "",
        nodeUnit: dataSetsArray[1]?.[0]?.unit_format,
        numberOfRuns: 1,
      })
      scatterChartOptons.scales.y.ticks = {
        callback: function (value: string) {
          return formatValuePythonic(value, formats.get(dataSetsArray[1]?.[0]?.node_key || ""))
        },
      }
    }

    return (
      <>
        <Grid item={true} xs={12} ref={ref}>
          <Box>
            <Typography variant="h6" textAlign="center" sx={{ mb: 1 }}>
              Nodes Correlation Chart for{" "}
              <b>
                {dataSetsArray.length === 2
                  ? // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
                    `${dataSetsArray[0][0]?.node_key} - ${dataSetsArray[1][0]?.node_key}`
                  : ""}
              </b>{" "}
              {version ? `Version ${version}` : ""}
            </Typography>
            {dataSetsArray.length == 2 && (
              <Scatter options={scatterChartOptons} data={chartData} height={100} />
            )}
            {dataSetsArray.length > 2 && (
              <Grid container={true}>
                {dataSetsArray.map((item, indexX) => {
                  return dataSetsArray.map((item, indexY) => {
                    const opt = structuredClone(scatterChartOptons)
                    const annotations = opt.plugins.annotation.annotations
                    if (multiChartData?.[indexX]?.[indexY]) {
                      annotations.line1.label.content =
                        multiChartData[indexX][indexY].annotationX?.content
                      annotations.line2.label.content =
                        multiChartData[indexX][indexY].annotationY?.content
                      annotations.line1 = {
                        ...annotations.line1,
                        ...multiChartData[indexX][indexY].annotationX,
                      }
                      annotations.line2 = {
                        ...annotations.line2,
                        ...multiChartData[indexX][indexY].annotationY,
                      }

                      opt.scales.x.title.text = generateChartLabel({
                        nodeName: dataSetsArray[indexX]?.[0]?.node_key ?? "",
                        nodeUnit: dataSetsArray[indexX]?.[0]?.unit_format,
                        numberOfRuns: 1,
                      })
                      opt.scales.x.ticks = {
                        callback: function (value: string) {
                          return formatValuePythonic(
                            value,
                            formats.get(dataSetsArray[indexX]?.[0]?.node_key || ""),
                          )
                        },
                      }
                      opt.scales.y.title.text = generateChartLabel({
                        nodeName: dataSetsArray[indexY]?.[0]?.node_key ?? "",
                        nodeUnit: dataSetsArray[indexY]?.[0]?.unit_format,
                        numberOfRuns: 1,
                      })
                      opt.scales.y.ticks = {
                        callback: function (value: string) {
                          return formatValuePythonic(
                            value,
                            formats.get(dataSetsArray[indexY]?.[0]?.node_key || ""),
                          )
                        },
                      }
                    }
                    return (
                      <Grid
                        key={indexX * 10 + indexY}
                        item={true}
                        xs={Math.floor(12 / dataSetsArray.length)}
                      >
                        {indexX !== indexY ? (
                          multiChartData?.[indexX]?.[indexY] && (
                            <Scatter options={opt} data={multiChartData[indexX][indexY]} />
                          )
                        ) : (
                          <Typography
                            sx={{
                              textAlign: "center",
                              position: "relative",
                              top: "50%",
                            }}
                          >
                            {item[indexX]
                              ? generateChartLabel({
                                  nodeName: item[indexX].node_key,
                                  nodeUnit: item[indexX].unit_format,
                                  numberOfRuns: 1,
                                })
                              : ""}
                          </Typography>
                        )}
                      </Grid>
                    )
                  })
                })}
              </Grid>
            )}
          </Box>
        </Grid>
      </>
    )
  },
)

CorrelogramScatter.displayName = "CorrelogramScatter"
